/**
 * A button that sits in the header bar to activate dropdowns.
 */

import {StyleSheet} from "aphrodite";
import * as React from "react";
import {findDOMNode} from "react-dom";

import Clickable from "@khanacademy/wonder-blocks-clickable";
import {type StyleType} from "@khanacademy/wonder-blocks-core";

type Props = {
    // Accessible name to use for the button. Used to describe this element
    // to screen readers.
    ["aria-label"]?: string;
    // Whether the menu this button controls is open. Used to tell screen readers when
    // the menu opens and closes.
    dropdownIsOpen?: boolean;
    // Callback function that allows the LearnMenu to capture the reference to
    // the actual element for the purpose of focusing on open/close.
    innerRef: (arg1?: HTMLElement | null | undefined) => unknown;
    // Extra styles to apply to the button.
    buttonStyles?: StyleType;
    // The child elements to put in the button.
    children: React.ReactNode;
    // Called when the button is pressed.
    // We also use this for keyboard Enter and Spacebar key presses because,
    // for keyboard users, that's a way of clicking the button.
    onClick: () => unknown;
    // Called when the ESC key is pressed while this button is focused.
    // This should be a function that closes the dropdown menu.
    onEscape: () => unknown;
    // Called when an down arrow event happens on the dropdown button
    onArrowDown: (arg1: React.KeyboardEvent) => unknown;
    // Called with an up arrow event happens on the dropdown button
    onArrowUp: (arg1: React.KeyboardEvent) => unknown;
    // The test ID to add to the button.
    testId?: string;
};

export default class DropdownButton extends React.Component<Props> {
    static defaultProps: {
        buttonStyles: Array<any>;
        innerRef: () => void;
        onArrowDown: () => void;
        onClick: () => void;
        onEscape: () => void;
    } = {
        innerRef: () => {},
        buttonStyles: [],
        onClick: () => {},
        onEscape: () => {},
        onArrowDown: () => {},
    };

    componentDidMount() {
        const node: HTMLElement = findDOMNode(this) as any;
        this.props.innerRef(node);
    }

    // We use keyUp and keyDown because there are some default behaviors we want to make consistent in the header
    handleKeyUp: (event: React.KeyboardEvent) => void = (
        event: React.KeyboardEvent,
    ) => {
        const {onClick, onEscape, onArrowDown, onArrowUp} = this.props;
        switch (event.key) {
            case "Enter":
            case " ":
                event.preventDefault();
                onClick();
                return;
            case "Escape":
                event.preventDefault();
                onEscape();
                return;
            case "Tab":
                // We explicitly don't use event.preventDefault() here because
                // we want the user to tab to the next element.
                onEscape();
                return;
            case "ArrowDown":
                onArrowDown(event);
                return;
            case "ArrowUp":
                onArrowUp(event);
                return;
        }
    };

    handleKeyDown: (event: React.KeyboardEvent) => void = (
        event: React.KeyboardEvent,
    ) => {
        // prevents the sreen from scrolling when opening the dropdown
        if (event.key === "ArrowDown") {
            event.preventDefault();
        }
    };

    render(): React.ReactNode {
        return (
            <Clickable
                testId={this.props.testId}
                style={[styles.button, this.props.buttonStyles]}
                aria-label={this.props["aria-label"]}
                aria-expanded={this.props.dropdownIsOpen}
                onClick={this.props.onClick}
                onKeyUp={this.handleKeyUp}
                onKeyDown={this.handleKeyDown}
            >
                {() => this.props.children}
            </Clickable>
        );
    }
}

const styles = StyleSheet.create({
    button: {
        alignItems: "center",
        cursor: "pointer",
        display: "flex",
        justifyContent: "center",
        paddingLeft: 0,
        paddingRight: 0,
        textDecoration: "none",
    },
});
